import React, { ChangeEventHandler, useContext } from "react";
import "./GamifiedQuiz.css";
import Select, { StylesConfig } from "react-select";
import QuestContext from "../../../../components/QuestWrapper";
import { extractGradientColors } from "../../../../general";

type ExtendedCSSProperties = React.CSSProperties & {
  background?: string;
  backgroundColor?: string;
  fontSize?: string | number;
  borderWidth?: string;
  borderColor?: string;
  color?: string;
  fontFamily?: string;
  flexDirection?: string;
  gap?: string;
  borderRadius?: string;
  border?: string;
  width?: string;
  padding?: string;
  cursor?: string;
};

interface SingleChoiceProps {
  options: string[] | any;
  checked: string;
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined;
  style?: ExtendedCSSProperties;
  selectedStyle?: {
    accentColor?: string;
    borderColor?: string;
    color?: string;
  };
  type?: "modal1" | "modal2" | "modal3" | string;
  hoverBackground?: string | any;
}

const SingleChoice: React.FC<SingleChoiceProps> = ({
  options,
  checked,
  onChange,
  style,
  selectedStyle = { accentColor: "#6525B3" },
  type,
  hoverBackground,
}) => {
  const { themeConfig } = useContext(QuestContext.Context);

  const handleClick = (option: string) => {
    const event = {
      target: {
        value: option,
        type: "radio",
        checked: true,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(event);
  };

  const containerStyle: ExtendedCSSProperties = {
    ...style,
    flexDirection: type === "modal2" ? "column" : "row",
    gap: type === "modal2" ? "12px" : "8px",
  };

  const customStyles: StylesConfig<any, false, any> = {
    control: (base) => ({
      ...base,
      background:
        containerStyle?.background ||
        containerStyle?.backgroundColor ||
        "transparent",
      fontSize: containerStyle?.fontSize || "14px",
      border: `${containerStyle?.borderWidth || "1px"} solid ${
        containerStyle?.borderColor ||
        themeConfig?.borderColor ||
        "var(--neutral-grey-100, #ECECEC)"
        }`,
      borderRadius: "10px",
      color: containerStyle?.color || themeConfig?.primaryColor || "black",
      fontFamily:
        containerStyle?.fontFamily ||
        themeConfig?.fontFamily ||
        "'Figtree', sans-serif",
      ...containerStyle,
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused
        ? hoverBackground || "#ADD8E6"
        : containerStyle?.background ||
        containerStyle?.backgroundColor ||
        "#f9fafb",
      color: containerStyle?.color || "black",
      fontSize: containerStyle?.fontSize || "14px",
      fontFamily:
        containerStyle?.fontFamily ||
        themeConfig?.fontFamily ||
        "'Figtree', sans-serif",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor:
        containerStyle?.background ||
        containerStyle?.backgroundColor ||
        "#f9fafb",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: containerStyle?.color || themeConfig?.primaryColor || "black",
      fontSize: containerStyle?.fontSize || "14px",
      fontFamily:
        containerStyle?.fontFamily ||
        themeConfig?.fontFamily ||
        "'Figtree', sans-serif",
    }),
  };

  return type === "modal3" ? (
    <div>
      <Select
        isSearchable={true}
        name={"selectbox"}
        options={options.map((opt: string) => ({ value: opt, label: opt }))}
        onChange={onChange}
        styles={customStyles}
        value={checked ? { value: checked, label: checked } : ""}
      />
    </div>
  ) : (
    <div className="q-singlechoice-input-cont_G" style={containerStyle}>
      {options.map((option: string, id: number) => {
        let borderColor = selectedStyle?.borderColor ||
          selectedStyle?.accentColor ||
          "#6525B3";
        if (borderColor.includes("gradient")) {
          borderColor = extractGradientColors(borderColor);
        }
        let radioColor = selectedStyle?.accentColor || "#6525B3";
        if (radioColor.includes("gradient")) {
          radioColor = extractGradientColors(radioColor);
        }
        let labelColor = selectedStyle?.color || selectedStyle?.accentColor || "#6525B3";
        if (labelColor.includes("gradient")) {
          labelColor = extractGradientColors(labelColor);
        }

        const labelContainerStyle: ExtendedCSSProperties = {
          border:
            checked === option
              ? `1px solid ${borderColor}`
              : `1px solid ${style?.borderColor || "#EFEFEF"}`,
          width: type === "modal2" ? "100%" : "fit-content",
          padding: type === "modal2" ? "10px 16px" : "8px 16px",
          gap: type === "modal2" ? "8px" : "4px",
          cursor: "pointer",
        };

        return (
          <div
            className="q_singlehoice_label_cont_G"
            key={id}
            style={labelContainerStyle}
            onClick={() => handleClick(option)}
          >
            <input
              type="radio"
              value={option}
              checked={checked === option}
              readOnly
              className="q-singlechoice-input_G"
              style={{ accentColor: radioColor }}
              id={option}
            />
            <label
              style={{
                color:
                  checked === option
                    ? labelColor
                    : style?.color || "#2c2c2c",
                cursor: "pointer",
              }}
              htmlFor={option}
              onClick={() => handleClick(option)}
            >
              {option}
            </label>
          </div>
        );
      })}
    </div>
  );
};

export default SingleChoice;
